/*eslint max-lines-per-function: ["error", 180]*/
import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  CreateUpdateCardDto,
  GetArticleDto,
  GetCardDto,
  GetExamDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  ArticleMode,
  CardType,
  TemplateType,
} from '../../../common/spdCore/validation/schemas';
import { ExamInfo } from '../../components/ExamJudgments/ExamInfo';
import { useNavigation } from '../../../common/navigation';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useAuth } from '../../../common/hooks/useAuth';
import { CardEditor } from '../../components/ExamCards/CardEditor';

type ParamTypes = {
  type: TemplateType;
  id: string;
};

export const ExamCardsView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const szafir = useSzafir();
  const auth = useAuth();
  const [exam, setExam] = useState<GetExamDto | null | undefined>();
  const [card, setCard] = useState<GetCardDto | undefined>();
  const [newCard, setNewCard] = useState<CreateUpdateCardDto | undefined>();
  const [allArticles, setAllArticles] = useState<GetArticleDto[]>([]);
  const [judgmentsPsychologistName, setJudgmentsPsychologistName] =
    useState<string>('Orzeczenia psychologiczne');
  const [judgmentsDoctorName, setJudgmentsDoctorName] = useState<string>(
    'Orzeczenia lekarskie',
  );

  useEffect(() => {
    if (auth.canSign) {
      szafir.activate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.canSign]);

  const { type, id } = useParams<ParamTypes>() as {
    type: TemplateType;
    id: string;
  };

  useEffect(() => {
    let mounted = true;
    const cardType =
      type === TemplateType.Psychological_Exam_card
        ? CardType.Psychologist
        : type === TemplateType.Weapon_psychological_exam_card
          ? CardType.PsychologistWeapon
          : type === TemplateType.Preventive_Exam_card
            ? CardType.Preventive
            : type === TemplateType.Sanitary_Exam_card
              ? CardType.Sanitary
              : type === TemplateType.Sport_card
                ? CardType.Sport
                : type === TemplateType.Weapon_card
                  ? CardType.Weapon
                  : CardType.Driver;

    spd.exams
      .getExam(id)
      .then(examData => {
        if (mounted) {
          setExam(examData);
          spd.cards
            .getCardByExam(id, cardType)
            .then(cardData => {
              if (mounted) {
                setCard(cardData);
                spd.cards
                  .cardData(id, type, cardData, examData?.endDate)
                  .then(r => setNewCard(r))
                  .catch(error =>
                    console.log(`Wystąpił problem z pobraniem karty. ${error}`),
                  );
              }
            })
            .catch(() => {
              setCard(undefined);
              spd.cards
                .cardData(id, type, undefined, examData?.endDate)
                .then(r => setNewCard(r))
                .catch(error =>
                  console.log(`Wystąpił problem z pobraniem karty. ${error}`),
                );
            });
        }
      })
      .catch(error =>
        console.log(`Wystąpił problem z odświeżeniem strony. ${error}`),
      );

    return () => {
      mounted = false;
    };
  }, [id, spd.cards, spd.exams, type]);

  useEffect(() => {
    spd.articles
      .getAll()
      .then(article => setAllArticles(article))
      .catch(error =>
        console.log(`Wystąpił problem z pobraniem podstaw prawnych. ${error}`),
      );
  }, [spd.articles]);

  useEffect(() => {
    if (id) {
      spd.judgments
        .getForExam(id)
        .then(o => {
          const judPsychoCount = o.filter(
            j =>
              (j?.article?.mode as unknown as ArticleMode) ===
              ArticleMode.Psychologist,
          ).length;
          const judDoctorCount = o.length - judPsychoCount;
          setJudgmentsPsychologistName(
            `Orzeczenia psychologiczne 
      ${(judPsychoCount > 0 && `(${judPsychoCount})`) || ''}`,
          );
          setJudgmentsDoctorName(`Orzeczenia lekarskie 
    ${(judDoctorCount > 0 && `(${judDoctorCount})`) || ''}`);
        })
        .catch(() => console.log('Nie udało się pobrać orzeczeń'));
    }
  }, [spd.judgments, id]);

  if (!newCard) {
    return null;
  }
  return (
    <>
      {exam && (
        <Row>
          <Col className="col-lg-4 col-12 mb-1">
            <ExamInfo
              exam={exam}
              articles={allArticles}
              mode={
                type === TemplateType.Psychological_Exam_card ||
                type === TemplateType.Weapon_psychological_exam_card
                  ? ArticleMode.Psychologist
                  : ArticleMode.Doctor
              }
            />
            {(type === TemplateType.Psychological_Exam_card ||
              type === TemplateType.Weapon_psychological_exam_card) && (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  void nav.examPsychologistJudgments(id);
                }}
                variant="outline-secondary"
                className="ms-1 mb-1"
              >
                {judgmentsPsychologistName}
              </Button>
            )}
            {type !== TemplateType.Psychological_Exam_card &&
              type !== TemplateType.Weapon_psychological_exam_card && (
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    void nav.examDoctorJudgments(id);
                  }}
                  variant="outline-secondary"
                  className="ms-1 mb-1"
                >
                  {judgmentsDoctorName}
                </Button>
              )}
          </Col>
          <Col className="col-lg-8 col-12 mb-1">
            <CardEditor exam={exam} card={newCard} id={card?.id} type={type} />
          </Col>
        </Row>
      )}
    </>
  );
};
